import { Navigate, Route, Routes, NavLink, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './lib/api';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ClientsList } from './pages/ClientsList';
import { ClientDetail } from './pages/ClientDetail';
import { Manifest } from './pages/Manifest';
import { Files } from './pages/Files';
import { Enrollment } from './pages/Enrollment';
import { Audit } from './pages/Audit';
import { Signing } from './pages/Signing';
import { ErrorState } from './components/QueryState';

type Me = { authenticated: boolean; actor?: string };

function useMe() {
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: () =>
      api.get<Me>('/me').catch((e) => {
        if ((e as { status?: number }).status === 401) return { authenticated: false };
        throw e;
      }),
  });
}

export function App() {
  const me = useMe();

  if (me.isLoading) {
    return <div className="p-6 text-muted">Loading…</div>;
  }
  if (me.isError) {
    return <div className="p-6"><ErrorState error={me.error} onRetry={() => me.refetch()} /></div>;
  }

  if (!me.data?.authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Shell actor={me.data.actor || 'admin'}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clients" element={<ClientsList />} />
        <Route path="/clients/:uuid" element={<ClientDetail />} />
        <Route path="/manifest" element={<Manifest />} />
        <Route path="/files" element={<Files />} />
        <Route path="/enrollment" element={<Enrollment />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/signing" element={<Signing />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

function Shell({ children, actor }: { children: React.ReactNode; actor: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const logout = useMutation({
    mutationFn: () => api.post('/logout'),
    onSuccess: () => {
      qc.setQueryData(['me'], { authenticated: false });
      nav('/login', { replace: true });
    },
  });
  const link = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'px-3 py-2 rounded-md text-sm font-medium bg-accent/10 text-accent'
      : 'px-3 py-2 rounded-md text-sm font-medium text-fg hover:bg-muted/10';
  return (
    <div className="min-h-full flex">
      <aside className="w-56 border-r border-border p-4 flex flex-col gap-1">
        <div className="font-semibold text-lg mb-4">Host Customize</div>
        <NavLink end to="/" className={link}>Dashboard</NavLink>
        <NavLink to="/clients" className={link}>Clients</NavLink>
        <NavLink to="/manifest" className={link}>Manifest</NavLink>
        <NavLink to="/files" className={link}>Files</NavLink>
        <NavLink to="/enrollment" className={link}>Enrollment</NavLink>
        <NavLink to="/signing" className={link}>Signing</NavLink>
        <NavLink to="/audit" className={link}>Audit</NavLink>
        <div className="mt-auto pt-4 text-xs text-muted">
          <div>Signed in as {actor}</div>
          <button className="btn mt-2 w-full" onClick={() => logout.mutate()}>
            Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 overflow-auto">{children}</main>
    </div>
  );
}
